import React from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';

const {FiTarget,FiCalendar,FiFileText,FiBriefcase,FiExternalLink} = FiIcons;

const SalesTeamPerformanceWidget = () => {
  const {data, salesTeamYear, salesTeamAvailableYears, setSalesTeamYear} = useAuth0Data();

  // Filter sales team members who are NOT client coordinators
  const salesTeamMembers = data.salesTeam.filter(member =>
    member.role !== 'client-coordinator'
  );

  // Generate year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [...new Set([...salesTeamAvailableYears, currentYear])].sort((a, b) => b - a);

  const handleYearChange = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSalesTeamYear(parseInt(e.target.value));
  };

  const getPerformanceColor = (current,goal) => {
    if (goal === 0) return 'text-gray-600 bg-gray-100';
    const percentage = (current / goal) * 100;
    if (percentage >= 90) return 'text-green-600 bg-green-100';
    if (percentage >= 75) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const isBusinessDevelopmentDirector = (role) => role === 'business-development-director';

  if (salesTeamMembers.length === 0) {
    return (
      <Link to="/data-management/sales-team" className="block">
        <motion.div
          whileHover={{y: -2}}
          className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTarget} className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="ml-3 text-lg font-semibold text-gray-900">Sales Team Performance</h3>
              <select
                value={salesTeamYear}
                onChange={handleYearChange}
                onClick={(e) => e.preventDefault()}
                className="ml-3 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-medium bg-white cursor-pointer"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <div className="text-center py-12 text-gray-500">
            <SafeIcon icon={FiTarget} className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No sales team members found for {salesTeamYear}.</p>
            <p className="text-sm">Click to add sales team members</p>
          </div>
        </motion.div>
      </Link>
    );
  }

  return (
    <Link to="/data-management/sales-team" className="block">
      <motion.div
        whileHover={{y: -2}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6 hover:shadow-md transition-all duration-200 cursor-pointer group"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="ml-3 text-lg font-semibold text-gray-900">Sales Team Performance</h3>
            <select
              value={salesTeamYear}
              onChange={handleYearChange}
              onClick={(e) => e.preventDefault()}
              className="ml-3 px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-medium bg-white cursor-pointer"
            >
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <SafeIcon icon={FiExternalLink} className="w-4 h-4 text-blue-600" />
          </div>
        </div>

        <div className="space-y-6">
          {salesTeamMembers.map((member) => (
            <div key={member.id} className="border border-gray-100 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-4">{member.name}</h4>
              <p className="text-xs text-gray-500 mb-3">{member.role === 'business-development-director' ? 'BD Director' : 'BD Manager'}</p>
              
              {isBusinessDevelopmentDirector(member.role) ? (
                // Simplified metrics for Business Development Director - only MSAs
                <div className="grid grid-cols-1 gap-4">
                  <div className="text-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 ${getPerformanceColor(member.msas || 0,member.msasGoal || 0)}`}>
                      <SafeIcon icon={FiFileText} className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {member.msas || 0}/{member.msasGoal || 0}
                    </p>
                    <p className="text-xs text-gray-500">MSAs</p>
                  </div>
                </div>
              ) : (
                // Full metrics for Business Development Manager
                <div className="grid grid-cols-3 gap-4">
                  {/* Meetings */}
                  <div className="text-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 ${getPerformanceColor(member.meetings || 0,member.meetingsGoal || 0)}`}>
                      <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {member.meetings || 0}/{member.meetingsGoal || 0}
                    </p>
                    <p className="text-xs text-gray-500">Meetings</p>
                  </div>

                  {/* MSAs */}
                  <div className="text-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 ${getPerformanceColor(member.msas || 0,member.msasGoal || 0)}`}>
                      <SafeIcon icon={FiFileText} className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {member.msas || 0}/{member.msasGoal || 0}
                    </p>
                    <p className="text-xs text-gray-500">MSAs</p>
                  </div>

                  {/* Jobs */}
                  <div className="text-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 ${getPerformanceColor(member.jobs || 0,member.jobsGoal || 0)}`}>
                      <SafeIcon icon={FiBriefcase} className="w-4 h-4" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {member.jobs || 0}/{member.jobsGoal || 0}
                    </p>
                    <p className="text-xs text-gray-500">Jobs</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          {isBusinessDevelopmentDirector(salesTeamMembers[0]?.role) ? (
            // Simplified totals for BD Directors
            <div className="grid grid-cols-1 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">Total MSAs</p>
                <p className="text-xl font-bold text-blue-600">
                  {salesTeamMembers.reduce((sum,member) => sum + (member.msas || 0),0)}
                </p>
              </div>
            </div>
          ) : (
            // Full totals for BD Managers
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-gray-500">Total Meetings</p>
                <p className="text-xl font-bold text-blue-600">
                  {salesTeamMembers.reduce((sum,member) => sum + (member.meetings || 0),0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total MSAs</p>
                <p className="text-xl font-bold text-blue-600">
                  {salesTeamMembers.reduce((sum,member) => sum + (member.msas || 0),0)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Jobs</p>
                <p className="text-xl font-bold text-blue-600">
                  {salesTeamMembers.reduce((sum,member) => sum + (member.jobs || 0),0)}
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </Link>
  );
};

export default SalesTeamPerformanceWidget;